import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, X, Clock, AlertCircle, CheckCircle2, Users, Printer, FileSpreadsheet, ClipboardList, CalendarX, UserX, ArrowLeftRight, ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, Repeat, History, Sparkles, Pencil, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Read the logged-in user once at render time. We only need role + token here.
const readUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); }
  catch { return null; }
};

// Annotation pill styling — distinct from regular shift pills and the
// italic "Off" state. Each type has its own color + icon + short label.
const ANNOTATION_STYLE = {
  requested_off: {
    label: 'Req Off',
    bg: '#fde68a',                 // amber-200
    border: '#f59e0b',             // amber-500
    text: '#78350f',               // amber-900
    iconBg: '#fbbf24',             // amber-400
  },
  needs_cover: {
    label: 'Cover',
    bg: '#fed7aa',                 // orange-200
    border: '#f97316',             // orange-500
    text: '#7c2d12',               // orange-900
    iconBg: '#fb923c',             // orange-400
  },
  swap_pending: {
    label: 'Swap',
    bg: '#bfdbfe',                 // blue-200
    border: '#3b82f6',             // blue-500
    text: '#1e3a8a',               // blue-900
    iconBg: '#60a5fa',             // blue-400
  },
  // Completed RSS shift switch — visually distinct from the in-progress
  // swap_pending state. Purple = "historical record / accountability".
  completed_swap: {
    label: 'Switched',
    bg: '#ede9fe',                 // violet-100
    border: '#7c3aed',             // violet-600
    text: '#4c1d95',               // violet-900
    iconBg: '#a78bfa',             // violet-400
  },
};

// Visual treatment for manual (custom) shifts. Deliberately distinct from the
// 4 standard color-coded pills so admins can spot a custom shift instantly.
const MANUAL_STYLE = {
  bg: '#fef3c7',         // amber-100
  border: '#d97706',     // amber-600 (border)
  text: '#78350f',       // amber-900
  pillBg: '#92400e',     // amber-800 (pill background — high contrast)
};

// Compute a rough hour count for a manual shift. Used in the weekly hours
// tally so admins still see hours when staff are on custom shifts. Returns
// 0 when times can't be parsed (we don't want to throw inside the grid).
const computeManualHours = (start, end) => {
  if (!start || !end) return 0;
  const parse = (val) => {
    if (typeof val !== 'string') return null;
    const s = val.trim().toUpperCase().replace(/\./g, '');
    // 12h: "10:00 AM" / "2:00 PM" / "10 AM" / 24h: "14:00"
    let m = s.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)$/);
    if (m) {
      let h = parseInt(m[1], 10);
      const mm = m[2] ? parseInt(m[2], 10) : 0;
      const ap = m[3];
      if (ap === 'PM' && h !== 12) h += 12;
      if (ap === 'AM' && h === 12) h = 0;
      return h + mm / 60;
    }
    m = s.match(/^(\d{1,2}):(\d{2})$/);
    if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
    return null;
  };
  const a = parse(start);
  const b = parse(end);
  if (a === null || b === null) return 0;
  // Allow overnight wrap-around (e.g. 11 PM → 3 AM)
  let h = b - a;
  if (h <= 0) h += 24;
  return Math.round(h * 10) / 10;
};

// Canonical shift windows in HOURS (with overnight lifted past midnight).
// Used to decide whether a manual shift fully covers at least one slot, in
// which case it earns a "Counts as Coverage" badge in the UI.
const STANDARD_SHIFT_WINDOWS_H = {
  day:       [7,  15],
  swing:     [11, 19],
  evening:   [15, 23],
  overnight: [23, 31],
};

// Returns the list of canonical shift_type keys this manual shift fully
// covers (e.g., a Swing manual 11AM-7PM returns ['swing']). When non-empty,
// the manual shift earns a "Counts as Coverage" indicator.
const manualShiftCoverageSlots = (m) => {
  if (!m || !m.start_time || !m.end_time) return [];
  const parse = (val) => {
    if (typeof val !== 'string') return null;
    const s = val.trim().toUpperCase().replace(/\./g, '');
    let mm = s.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)$/);
    if (mm) {
      let h = parseInt(mm[1], 10);
      const min = mm[2] ? parseInt(mm[2], 10) : 0;
      const ap = mm[3];
      if (ap === 'PM' && h !== 12) h += 12;
      if (ap === 'AM' && h === 12) h = 0;
      return h + min / 60;
    }
    mm = s.match(/^(\d{1,2}):(\d{2})$/);
    if (mm) return parseInt(mm[1], 10) + parseInt(mm[2], 10) / 60;
    return null;
  };
  const s = parse(m.start_time);
  let e = parse(m.end_time);
  if (s === null || e === null) return [];
  if (e <= s) e += 24; // overnight wrap
  const covered = [];
  Object.entries(STANDARD_SHIFT_WINDOWS_H).forEach(([key, [rs, re]]) => {
    if (s <= rs && e >= re) covered.push(key);
  });
  return covered;
};

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Map of shift_type → short code shown in cells
const SHIFT_CODE = {
  day: '7a-3p',
  swing: '11a-7p',
  evening: '3p-11p',
  overnight: '11p-7a',
};

// Choices presented in the cell dialog (assign {member} on {date} to which shift?)
const SHIFT_CHOICES = [
  { type: 'day',       label: 'Day',       code: '7a-3p',  time: '7:00 AM – 3:00 PM',  optional: false },
  { type: 'swing',     label: 'Swing',     code: '11a-7p', time: '11:00 AM – 7:00 PM', optional: true  },
  { type: 'evening',   label: 'Evening',   code: '3p-11p', time: '3:00 PM – 11:00 PM', optional: false },
  { type: 'overnight', label: 'Overnight', code: '11p-7a', time: '11:00 PM – 7:00 AM', optional: false },
];

// Roster display order — also used by the printable HTML
const ROSTER_ORDER = [
  'Marvay Taylor', 'Hurieya Morton', 'Keisha Bailey',
  'Akaylah', 'Senita', 'Doreena', 'Brittany',
];

// One distinct light color per staff member (consistent across cells, name pill, and pill bg)
const STAFF_COLORS = {
  'Marvay Taylor':  { row: '#e3f2fd', name: '#bbdefb', text: '#0d47a1' }, // blue
  'Hurieya Morton': { row: '#e8f5e9', name: '#c8e6c9', text: '#1b5e20' }, // green
  'Keisha Bailey':  { row: '#f3e5f5', name: '#e1bee7', text: '#4a148c' }, // purple
  'Akaylah':        { row: '#fce4ec', name: '#f8bbd0', text: '#880e4f' }, // pink
  'Senita':         { row: '#fff8e1', name: '#ffecb3', text: '#7c5800' }, // amber
  'Doreena':        { row: '#e0f7fa', name: '#b2ebf2', text: '#006064' }, // teal
  'Brittany':       { row: '#fff3e0', name: '#ffe0b2', text: '#bf360c' }, // orange
  _default:         { row: '#f5f5f5', name: '#e0e0e0', text: '#424242' },
};

const Schedule = () => {
  const navigate = useNavigate();
  const [shiftDefinitions, setShiftDefinitions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Cell-click dialog state: { member, day, currentShifts: ShiftAssignment[] }
  const [isCellDialogOpen, setIsCellDialogOpen] = useState(false);
  const [cellContext, setCellContext] = useState(null);
  // Shift-level availability for the member+date in the open cell dialog
  const [cellAvailability, setCellAvailability] = useState(null);
  // Pending availability-conflict confirmation { shiftType, status, restriction }
  const [availabilityWarn, setAvailabilityWarn] = useState(null);
  // Pending unassign confirmation { shift, role } — clicking an already-selected
  // shift in the Assign modal toggles it off (after confirmation).
  const [unassignConfirm, setUnassignConfirm] = useState(null);
  // Double-shift confirmation dialog state:
  // { member, day, newShiftType, existingShifts }
  const [doubleConfirm, setDoubleConfirm] = useState(null);
  // Annotations for the current week, keyed by `${date}|${member_id}`.
  // Built from the GET /api/schedule/annotations/week response. Excludes
  // completed_swap entries — those are kept in `completedSwaps` (below) so
  // they can coexist with operational annotations on the same cell.
  const [annotations, setAnnotations] = useState({});
  // Completed shift-switch history. Keyed by `${date}|${member_id}|${shift}`
  // so multiple swaps on the same person/day (e.g. day + evening) coexist.
  const [completedSwaps, setCompletedSwaps] = useState({});
  // Local state for the proposed swap partner inside the cell modal
  const [swapPartnerId, setSwapPartnerId] = useState('');
  // Local state for the "Secondary staff" picker inside the cell modal
  const [secondaryPickerId, setSecondaryPickerId] = useState('');
  // "Record Completed Switch" form state inside the cell modal:
  // { shiftType, originalId, notes } — null when the form is closed.
  const [recordSwitch, setRecordSwitch] = useState(null);
  // Manual shifts for the current week, keyed by `${date}|${memberId}` —
  // value is an array because a single member can have several manual shifts
  // on the same day.
  const [manualShifts, setManualShifts] = useState({});
  // Inline manual-shift form state inside the cell modal. `null` = closed.
  // When `id` is present, the form is in EDIT mode for that shift.
  const [manualForm, setManualForm] = useState(null);
  // Swap-confirmation slot disambiguation (only used when a swap_pending
  // annotation is being confirmed AND the underlying shift is double-staffed).
  // Shape: { annotationId, primaryName, secondaryName }
  const [swapSlotPrompt, setSwapSlotPrompt] = useState(null);

  // Current logged-in user (used to gate the admin-only annotation buttons)
  const currentUser = readUser();
  const isAdmin = String(currentUser?.role || '').trim().toLowerCase() === 'admin';
  const authHeaders = currentUser?.token
    ? { Authorization: `Bearer ${currentUser.token}` }
    : {};

  // Get current date and calculate week start (Sunday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    d.setDate(d.getDate() - day); // Roll back to the most recent Sunday
    return d;
  };

  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate week dates from current week start
  const getWeekDates = () => {
    const dates = [];
    const weekStart = new Date(currentWeekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  // Format week range display
  const formatWeekRange = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const startMonth = monthNames[start.getMonth()];
    const endMonth = monthNames[end.getMonth()];
    const startDate = start.getDate();
    const endDate = end.getDate();
    const year = end.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDate} - ${endDate}, ${year}`;
    } else {
      return `${startMonth} ${startDate} - ${endMonth} ${endDate}, ${year}`;
    }
  };

  // Fetch shift definitions
  const fetchShiftDefinitions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shifts/definitions`);
      setShiftDefinitions(response.data);
    } catch (error) {
      console.error('Error fetching shift definitions:', error);
    }
  };

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // Fetch weekly shift data
  const fetchWeeklyShifts = async () => {
    setLoading(true);
    try {
      const weekStartStr = formatDate(currentWeekStart);
      const [shiftsRes, annRes, manRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/shifts/week/${weekStartStr}`),
        currentUser?.token
          ? axios.get(`${API_URL}/api/schedule/annotations/week/${weekStartStr}`, { headers: authHeaders })
          : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/api/manual-shifts/week/${weekStartStr}`),
      ]);
      if (shiftsRes.status === 'fulfilled') {
        setWeeklyData(shiftsRes.value.data);
      }
      const annList = annRes.status === 'fulfilled' ? (annRes.value.data || []) : [];
      const byKey = {};
      const swapsByKey = {};
      annList.forEach((a) => {
        if (a.shift_type === 'completed_swap') {
          const k = `${a.date}|${a.member_id}|${a.original_shift || ''}`;
          swapsByKey[k] = a;
        } else {
          byKey[`${a.date}|${a.member_id}`] = a;
        }
      });
      setAnnotations(byKey);
      setCompletedSwaps(swapsByKey);

      // Index manual shifts by (date, member). Both primary AND secondary
      // assignees see the shift on their row, so we push the same record
      // under both keys.
      const manList = manRes.status === 'fulfilled' ? (manRes.value.data || []) : [];
      const manualByKey = {};
      manList.forEach((m) => {
        if (m.assigned_rss_id) {
          const k = `${m.date}|${m.assigned_rss_id}`;
          (manualByKey[k] = manualByKey[k] || []).push(m);
        }
        if (m.secondary_rss_id && m.secondary_rss_id !== m.assigned_rss_id) {
          const k = `${m.date}|${m.secondary_rss_id}`;
          (manualByKey[k] = manualByKey[k] || []).push(m);
        }
      });
      setManualShifts(manualByKey);
    } catch (error) {
      console.error('Error fetching weekly shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchShiftDefinitions();
    fetchTeamMembers();
  }, []);

  // Reload shifts when week changes
  useEffect(() => {
    fetchWeeklyShifts();
  }, [currentWeekStart]);

  // Navigate to previous week
  const previousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  // Navigate to next week
  const nextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  // Go to current week
  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  // Open assign dialog when a grid cell is clicked
  const openCellDialog = (member, day, currentShifts) => {
    setCellContext({ member, day, currentShifts: currentShifts || [] });
    setSwapPartnerId('');
    setSecondaryPickerId('');
    setRecordSwitch(null);
    setManualForm(null);
    setIsCellDialogOpen(true);
    setCellAvailability(null);
    setAvailabilityWarn(null);
    if (isAdmin) {
      axios.get(`${API_URL}/api/availability/lookup/${member.id}/${day.date}`)
        .then((res) => setCellAvailability(res.data))
        .catch(() => setCellAvailability(null));
    }
  };

  // Persist an explicit primary+secondary pair on a working shift slot.
  // We always send both ids so the backend can update or clear cleanly.
  const setShiftStaff = async ({ date, shiftType, primaryId, secondaryId }) => {
    await axios.post(`${API_URL}/api/shifts/assign`, {
      date,
      shift_type: shiftType,
      assigned_rss_id: primaryId || null,
      secondary_rss_id: secondaryId || null,
      notes: null,
    });
    await fetchWeeklyShifts();
  };

  const handleAddSecondary = async (shift, secondaryId) => {
    if (!cellContext || !shift || !secondaryId) return;
    if (secondaryId === cellContext.member.id || secondaryId === shift.assigned_rss_id) {
      alert('Same person cannot fill both slots.');
      return;
    }
    try {
      await setShiftStaff({
        date: cellContext.day.date,
        shiftType: shift.shift_type,
        primaryId: shift.assigned_rss_id || cellContext.member.id,
        secondaryId,
      });
      setSecondaryPickerId('');
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Add secondary failed', e);
      alert(e?.response?.data?.detail || 'Failed to add secondary staff.');
    }
  };

  const handleRemoveSecondary = async (shift) => {
    if (!cellContext || !shift) return;
    try {
      await setShiftStaff({
        date: cellContext.day.date,
        shiftType: shift.shift_type,
        primaryId: shift.assigned_rss_id,
        secondaryId: null,
      });
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Remove secondary failed', e);
      alert('Failed to remove secondary staff.');
    }
  };

  const handleConfirmUnassign = async () => {
    if (!unassignConfirm) return;
    const { shift, role } = unassignConfirm;
    try {
      await axios.delete(
        `${API_URL}/api/shifts/${shift.id}${role === 'secondary' ? '?target=secondary' : ''}`,
        { headers: authHeaders },
      );
      setUnassignConfirm(null);
      setIsCellDialogOpen(false);
      setCellContext(null);
      await fetchWeeklyShifts();
    } catch (err) {
      console.error('Error unassigning shift:', err);
      alert('Failed to unassign shift. Please try again.');
    }
  };

  // Internal: actually persist the assignment (POST), no conflict checks here
  const persistAssignment = async (member, day, shiftType, availabilityOverride = null) => {
    await axios.post(`${API_URL}/api/shifts/assign`, {
      date: day.date,
      shift_type: shiftType,
      assigned_rss_id: member.id,
      notes: null,
      availability_override: availabilityOverride,
    });
    await fetchWeeklyShifts();
  };

  // Availability status for a given shift type in the open cell dialog
  const cellShiftAvailability = (shiftType) => {
    if (!cellAvailability?.found) return null;
    if (cellAvailability.unavailable_all_day) return 'unavailable';
    return cellAvailability.shifts?.[shiftType] || 'no_response';
  };

  // Click a shift in the cell dialog. Conflict-prevention with double-shift override:
  //   - Already on this exact shift → no-op
  //   - Has another shift this day → show double-confirmation dialog
  //   - Otherwise → assign directly
  const handleCellAssign = async (shiftType, availabilityOverridden = false) => {
    if (!cellContext) return;
    const { member, day, currentShifts } = cellContext;

    // Already on this exact shift — toggle OFF: confirm, then unassign
    const existing = currentShifts.find((s) => s.shift_type === shiftType);
    if (existing) {
      const role = (existing.assigned_rss_id === member.id || existing.assigned_rss_name === member.name)
        ? 'primary' : 'secondary';
      setUnassignConfirm({ shift: existing, role });
      return;
    }

    // Availability conflict: staff marked this shift (or the whole day)
    // Unavailable. Warn + require confirmation; never block.
    const availStatus = cellShiftAvailability(shiftType);
    if (!availabilityOverridden && availStatus === 'unavailable') {
      setAvailabilityWarn({
        shiftType,
        status: availStatus,
        allDay: !!cellAvailability?.unavailable_all_day,
        restriction: cellAvailability?.restriction || '',
        note: cellAvailability?.note || '',
      });
      return;
    }
    const override = availabilityOverridden
      ? { status: availStatus, restriction: cellAvailability?.restriction || '', member: member.name, date: day.date }
      : null;

    // No conflict — assign immediately
    if (currentShifts.length === 0) {
      try {
        await persistAssignment(member, day, shiftType, override);
        setIsCellDialogOpen(false);
        setCellContext(null);
      } catch (err) {
        console.error('Error assigning shift:', err);
        alert('Failed to assign shift. Please try again.');
      }
      return;
    }

    // Conflict: surface the Confirm-Double dialog. The cell dialog stays
    // visually behind it until the user resolves the choice.
    setDoubleConfirm({
      member,
      day,
      newShiftType: shiftType,
      existingShifts: currentShifts,
      availabilityOverride: override,
    });
  };

  // User clicked "Confirm Double" in the double-shift warning dialog:
  // commit the new assignment WITHOUT vacating the existing one(s).
  const handleConfirmDouble = async () => {
    if (!doubleConfirm) return;
    try {
      await persistAssignment(doubleConfirm.member, doubleConfirm.day, doubleConfirm.newShiftType, doubleConfirm.availabilityOverride || null);
      setDoubleConfirm(null);
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (err) {
      console.error('Error confirming double:', err);
      alert('Failed to assign double shift. Please try again.');
    }
  };

  // Mark this member Off for this day — clear all of their assignments that day
  const handleCellRemove = async () => {
    if (!cellContext?.currentShifts?.length) {
      setIsCellDialogOpen(false);
      return;
    }
    try {
      for (const s of cellContext.currentShifts) {
        if (s.id) await axios.delete(`${API_URL}/api/shifts/${s.id}`);
      }
      await fetchWeeklyShifts();
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (error) {
      console.error('Error removing shifts:', error);
      alert('Failed to remove shift(s). Please try again.');
    }
  };

  // ==================== ANNOTATION HANDLERS (admin-only) ====================
  // Build the request body for the POST /api/schedule/annotations endpoint
  // based on the action chosen in the modal.
  const createAnnotation = async (shiftType, extras = {}) => {
    if (!cellContext) return;
    const { member, day, currentShifts } = cellContext;
    const firstShift = currentShifts?.[0];
    const body = {
      shift_type: shiftType,
      date: day.date,
      member_id: member.id,
      member_name: member.name,
      original_shift: firstShift?.shift_type || null,
      original_shift_id: firstShift?.id || null,
      ...extras,
    };
    try {
      await axios.post(`${API_URL}/api/schedule/annotations`, body, { headers: authHeaders });
      await fetchWeeklyShifts();
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Annotation failed', e);
      alert(e?.response?.data?.detail || 'Failed to save annotation.');
    }
  };

  const handleRequestedOff = () => createAnnotation('requested_off');
  const handleNeedsCover = () => createAnnotation('needs_cover');
  const handleProposeSwap = (partner) => {
    if (!partner) {
      alert('Pick a staff member to swap with.');
      return;
    }
    createAnnotation('swap_pending', {
      swap_with: partner.name,
      swap_with_id: partner.id,
    });
  };

  // Clear any annotation on this cell. Underlying shift_assignment is untouched.
  const handleClearAnnotation = async (annotationId) => {
    try {
      await axios.delete(`${API_URL}/api/schedule/annotations/${annotationId}`, { headers: authHeaders });
      await fetchWeeklyShifts();
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Clear annotation failed', e);
      alert(e?.response?.data?.detail || 'Failed to clear annotation.');
    }
  };

  // ===== Completed shift switches =====
  // Manually record a completed RSS shift switch. The backend atomically
  // reassigns the shift to the new RSS and creates a completed_swap
  // annotation that preserves the original assignment for the schedule UI.
  const submitRecordSwitch = async () => {
    if (!cellContext || !recordSwitch) return;
    const { member, day, currentShifts } = cellContext;
    const shiftType = recordSwitch.shiftType
      || currentShifts[0]?.shift_type
      || '';
    const originalId = recordSwitch.originalId;
    if (!shiftType) {
      alert('Pick which shift was switched.');
      return;
    }
    if (!originalId) {
      alert('Pick the original RSS who had the shift.');
      return;
    }
    if (originalId === member.id) {
      alert('Original RSS and new RSS cannot be the same person.');
      return;
    }
    const original = teamMembers.find((m) => m.id === originalId);
    if (!original) {
      alert('Original RSS not found.');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/schedule/swaps/record`,
        {
          date: day.date,
          shift_type: shiftType,
          original_member_id: original.id,
          original_member_name: original.name,
          new_member_id: member.id,
          new_member_name: member.name,
          notes: recordSwitch.notes || null,
        },
        { headers: authHeaders },
      );
      await fetchWeeklyShifts();
      setRecordSwitch(null);
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Record switch failed', e);
      alert(e?.response?.data?.detail || 'Failed to record shift switch.');
    }
  };

  // Clear a completed_swap annotation only. The underlying shift assignment
  // is intentionally untouched — the new RSS remains responsible for the
  // shift; the admin is just removing the history annotation from the grid.
  const handleClearCompletedSwap = async (annotationId) => {
    try {
      await axios.delete(`${API_URL}/api/schedule/annotations/${annotationId}`, { headers: authHeaders });
      await fetchWeeklyShifts();
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Clear completed_swap failed', e);
      alert(e?.response?.data?.detail || 'Failed to clear completed switch.');
    }
  };

  // ===== Manual shifts =====
  // Create or update a manual shift from the inline form inside the cell
  // modal. The form holds either an empty draft (id=null) or an existing
  // manual shift loaded for editing (id set).
  const submitManualShift = async () => {
    if (!cellContext || !manualForm) return;
    const { member, day } = cellContext;
    const label = (manualForm.label || '').trim();
    const start = (manualForm.start_time || '').trim();
    const end = (manualForm.end_time || '').trim();
    if (!label) { alert('Label is required.'); return; }
    if (!start) { alert('Start time is required.'); return; }
    if (!end)   { alert('End time is required.'); return; }
    if (manualForm.secondary_rss_id && manualForm.secondary_rss_id === member.id) {
      alert('Same staff member cannot be assigned twice.');
      return;
    }
    const body = {
      date: day.date,
      label,
      start_time: start,
      end_time: end,
      assigned_rss_id: member.id,
      secondary_rss_id: manualForm.secondary_rss_id || null,
      notes: manualForm.notes || null,
    };
    try {
      if (manualForm.id) {
        await axios.put(`${API_URL}/api/manual-shifts/${manualForm.id}`, body, { headers: authHeaders });
      } else {
        await axios.post(`${API_URL}/api/manual-shifts`, body, { headers: authHeaders });
      }
      await fetchWeeklyShifts();
      setManualForm(null);
    } catch (e) {
      console.error('Manual shift save failed', e);
      alert(e?.response?.data?.detail || 'Failed to save manual shift.');
    }
  };

  const deleteManualShift = async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this manual shift?')) return;
    try {
      await axios.delete(`${API_URL}/api/manual-shifts/${id}`, { headers: authHeaders });
      await fetchWeeklyShifts();
      setManualForm(null);
    } catch (e) {
      console.error('Manual shift delete failed', e);
      alert(e?.response?.data?.detail || 'Failed to delete manual shift.');
    }
  };

  // Confirm a swap_pending annotation: reassigns the original shift to the
  // swap partner server-side and clears the annotation. When the underlying
  // shift is double-staffed, we surface a slot picker first instead of
  // silently picking primary or secondary.
  const handleConfirmSwap = async (annotationId) => {
    const { day, currentShifts } = cellContext || {};
    const firstShift = currentShifts?.[0];
    const isDouble = !!(firstShift?.assigned_rss_id && firstShift?.secondary_rss_id);
    if (isDouble) {
      setSwapSlotPrompt({
        annotationId,
        primaryName: firstShift.assigned_rss_name,
        secondaryName: firstShift.secondary_rss_name,
      });
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/schedule/annotations/${annotationId}/confirm`,
        {},
        { headers: authHeaders },
      );
      await fetchWeeklyShifts();
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Confirm swap failed', e);
      alert(e?.response?.data?.detail || 'Failed to confirm swap.');
    }
  };

  // Called after admin picks which staff slot is being swapped (only used for
  // double-staffed shifts).
  const finalizeSwapWithSlot = async (slot) => {
    if (!swapSlotPrompt) return;
    try {
      await axios.post(
        `${API_URL}/api/schedule/annotations/${swapSlotPrompt.annotationId}/confirm`,
        { slot },
        { headers: authHeaders },
      );
      await fetchWeeklyShifts();
      setSwapSlotPrompt(null);
      setIsCellDialogOpen(false);
      setCellContext(null);
    } catch (e) {
      console.error('Confirm swap (with slot) failed', e);
      alert(e?.response?.data?.detail || 'Failed to confirm swap.');
    }
  };

  // Staff order on the Schedule grid + print export.
  // The backend sorts /api/team by `display_order` (admin-managed via the
  // up/down arrows below), so we just respect that order verbatim.
  const orderedRoster = teamMembers;

  // ==================== REORDER (admin only) ====================
  // Persist a new order on the backend. We send the FULL list of currently
  // active member ids so the server can renumber in one transaction.
  const saveOrder = async (newRoster) => {
    try {
      await axios.put(
        `${API_URL}/api/team/reorder`,
        { order: newRoster.map((m) => m.id) },
        { headers: authHeaders },
      );
      await fetchTeamMembers();
    } catch (e) {
      console.error('Reorder failed', e);
      alert(e?.response?.data?.detail || 'Failed to save new order.');
    }
  };

  const moveMember = (memberId, direction) => {
    const idx = orderedRoster.findIndex((m) => m.id === memberId);
    if (idx === -1) return;
    const next = [...orderedRoster];
    if (direction === 'up' && idx > 0) {
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    } else if (direction === 'down' && idx < next.length - 1) {
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    } else if (direction === 'top' && idx > 0) {
      const [item] = next.splice(idx, 1);
      next.unshift(item);
    } else if (direction === 'bottom' && idx < next.length - 1) {
      const [item] = next.splice(idx, 1);
      next.push(item);
    } else {
      return; // no-op at the boundary
    }
    saveOrder(next);
  };

  // ==================== PRINT & EXPORT ====================

  const SHIFT_DISPLAY_ORDER = ['day', 'swing', 'evening', 'overnight'];
  const SHIFT_LABELS = {
    day:       { name: 'Day',       time: '7:00 AM – 3:00 PM',   optional: false },
    swing:     { name: 'Swing',     time: '11:00 AM – 7:00 PM',  optional: true  },
    evening:   { name: 'Evening',   time: '3:00 PM – 11:00 PM',  optional: false },
    overnight: { name: 'Overnight', time: '11:00 PM – 7:00 AM',  optional: false },
  };

  // HTML-escape a string for safe injection into the print document
  const escapeHtml = (val) => {
    if (val === null || val === undefined) return '';
    return String(val)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Format a Date as a friendly long string e.g. "Monday, May 5, 2026"
  const formatLongDate = (date) =>
    date.toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });

  // Fetch all daily_coverage records that fall in the calendar month containing `refDate`
  // Returns `{ days, manualShifts }` — all daily_coverage records and all
  // manual shifts for the calendar month containing `refDate`.
  const fetchMonthCoverage = async (refDate) => {
    const year = refDate.getFullYear();
    const month = refDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);

    // Walk Monday-by-Monday across every week that touches this month
    const weeks = [];
    let cursor = getWeekStart(firstOfMonth);
    while (cursor <= lastOfMonth) {
      weeks.push(formatDate(cursor));
      const next = new Date(cursor);
      next.setDate(next.getDate() + 7);
      cursor = next;
    }

    // Fan out shift + manual-shift requests per week, in parallel.
    const responses = await Promise.all(
      weeks.flatMap((ws) => [
        axios.get(`${API_URL}/api/shifts/week/${ws}`),
        axios.get(`${API_URL}/api/manual-shifts/week/${ws}`),
      ])
    );

    // Flatten daily_coverage and keep only days that fall in the target month
    const days = [];
    const seen = new Set();
    const manualSeen = new Set();
    const manual = [];
    for (let i = 0; i < weeks.length; i++) {
      const shiftResp = responses[i * 2];
      const manualResp = responses[i * 2 + 1];
      shiftResp.data.daily_coverage.forEach((d) => {
        const dt = new Date(d.date + 'T00:00:00');
        if (dt.getFullYear() === year && dt.getMonth() === month && !seen.has(d.date)) {
          seen.add(d.date);
          days.push(d);
        }
      });
      (manualResp.data || []).forEach((m) => {
        const dt = new Date(m.date + 'T00:00:00');
        if (dt.getFullYear() === year && dt.getMonth() === month && !manualSeen.has(m.id)) {
          manualSeen.add(m.id);
          manual.push(m);
        }
      });
    }
    days.sort((a, b) => a.date.localeCompare(b.date));
    manual.sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || '').localeCompare(b.start_time || ''));
    return { days, manualShifts: manual };
  };

  // Build the printable HTML body — landscape staff-roster grid (Staff x 7 days)
  // `manualList` is an optional flat list of manual_shifts spanning the same
  // date range; they're rendered as extra rows inside each cell.
  const buildPrintHTML = (dateRangeLabel, days, manualList = []) => {
    // Group days into Sun-Sat weeks. Each week becomes one full-page table.
    const weeks = [];
    let currentWeek = null;
    days.forEach((d) => {
      const dt = new Date(d.date + 'T00:00:00');
      if (dt.getDay() === 0 || !currentWeek) {
        currentWeek = [];
        weeks.push(currentWeek);
      }
      currentWeek.push(d);
    });

    // Use the component-level orderedRoster (shared with the on-screen grid)
    const printRoster = orderedRoster;

    // Build one weekly table block
    const buildWeekTable = (weekDaysArr) => {
      const byDay = {};  // 0..6 (Sun..Sat) → daily_coverage record
      weekDaysArr.forEach((d) => {
        const wd = new Date(d.date + 'T00:00:00').getDay();
        byDay[wd] = d;
      });

      const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const dayCols = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

      // Print-only filter: hide staff with zero shifts this week. Counts standard
      // shifts (primary OR secondary — covers, swaps and switches live on the
      // assignment itself) plus manual shifts (primary OR secondary).
      const weekDates = weekDaysArr.map((d) => d.date);
      const weekRoster = printRoster.filter((member) =>
        weekDaysArr.some((d) => d.shifts.some(
          (s) =>
            s.assigned_rss_id === member.id
            || s.assigned_rss_name === member.name
            || s.secondary_rss_id === member.id
            || s.secondary_rss_name === member.name
        ))
        || manualList.some(
          (m) =>
            weekDates.includes(m.date)
            && (m.assigned_rss_id === member.id || m.secondary_rss_id === member.id)
        )
      );

      // Header row: Staff | Sun (Mon DD) | Mon (Mon DD) | ...
      const headerCells = dayCols.map((name, i) => {
        const d = byDay[i];
        const sub = d
          ? `<div class="day-date">${monthShort[new Date(d.date+'T00:00:00').getMonth()]} ${new Date(d.date+'T00:00:00').getDate()}</div>`
          : '<div class="day-date">—</div>';
        return `<th><div class="day-name">${name}</div>${sub}</th>`;
      }).join('');

      // For each staff member, find ALL shifts they're on for each day (or "Off")
      const staffRows = weekRoster.map((member, idx) => {
        const cells = [0,1,2,3,4,5,6].map((wd) => {
          const day = byDay[wd];
          if (!day) return '<td class="cell-empty">—</td>';
          // Include shifts where this member is EITHER primary or secondary,
          // so double-staffed slots show on both staff's rows.
          const myShifts = day.shifts.filter(
            (s) =>
              s.assigned_rss_id === member.id
              || s.assigned_rss_name === member.name
              || s.secondary_rss_id === member.id
              || s.secondary_rss_name === member.name
          );
          // Manual shifts the member is on for this date (primary OR secondary).
          const myManual = manualList.filter(
            (m) =>
              m.date === day.date
              && (m.assigned_rss_id === member.id || m.secondary_rss_id === member.id)
          );
          if (myShifts.length === 0 && myManual.length === 0) return '<td class="cell-off">Off</td>';
          // Order Day → Swing → Evening → Overnight for display
          const orderedShifts = SHIFT_DISPLAY_ORDER
            .map((t) => myShifts.find((s) => s.shift_type === t))
            .filter(Boolean);
          const codeLines = orderedShifts
            .map((s) => {
              // Identify the partner (other staff on this same shift) so we
              // can render their name compactly under the shift code.
              let partnerName = null;
              if (s.assigned_rss_id === member.id || s.assigned_rss_name === member.name) {
                partnerName = s.secondary_rss_name || null;
              } else if (s.secondary_rss_id === member.id || s.secondary_rss_name === member.name) {
                partnerName = s.assigned_rss_name || null;
              }
              const code = escapeHtml(SHIFT_CODE[s.shift_type] || s.shift_type);
              const partnerLine = partnerName
                ? `<div class="partner-name">+ ${escapeHtml(partnerName)}</div>`
                : '';
              return `<div>${code}</div>${partnerLine}`;
            })
            .join('');
          const doubleBadge = myShifts.length >= 2
            ? '<div class="double-badge">2x DOUBLE</div>'
            : '';
          // Manual shifts: rendered after the standard shift codes with a
          // distinct amber styling so supervisors can spot custom shifts
          // immediately on a printed page.
          const manualLines = myManual.map((m) => {
            const isSecondary = m.secondary_rss_id === member.id;
            const partner = isSecondary ? m.assigned_rss_name : m.secondary_rss_name;
            const partnerLine = partner
              ? `<div class="manual-partner">+ ${escapeHtml(partner)}</div>`
              : '';
            const notesLine = m.notes
              ? `<div class="manual-notes">${escapeHtml(m.notes)}</div>`
              : '';
            return `<div class="manual-shift">
              <div class="manual-label">${escapeHtml(m.label)}</div>
              <div class="manual-time">${escapeHtml(m.start_time)} – ${escapeHtml(m.end_time)}</div>
              ${partnerLine}
              ${notesLine}
            </div>`;
          }).join('');
          // If only manual shifts exist (no standard shift), still use cell-shift
          // styling so the cell isn't visually "Off".
          return `<td class="cell-shift">${codeLines}${doubleBadge}${manualLines}</td>`;
        }).join('');
        const rowClass = idx % 2 === 0 ? 'row-even' : 'row-odd';
        return `<tr class="${rowClass}">
          <td class="staff-name">${escapeHtml(member.name)}</td>
          ${cells}
        </tr>`;
      }).join('');

      // OPEN-shifts summary row: per day, list any uncovered REQUIRED shift codes
      // (Day, Evening, Overnight). Swing is optional and is intentionally not flagged
      // as a gap — this matches the on-screen OPEN Shifts row exactly.
      // Manual shifts that fully cover a slot mark it `is_covered`; partial
      // overlaps surface as a softer "PARTIAL X" line.
      const openCells = [0,1,2,3,4,5,6].map((wd) => {
        const day = byDay[wd];
        if (!day) return '<td class="cell-empty">—</td>';
        const required = day.shifts.filter((s) =>
          s.shift_type === 'day' || s.shift_type === 'evening' || s.shift_type === 'overnight'
        );
        const openCodes = required
          .filter((s) => !s.is_covered && !s.is_partial)
          .map((s) => SHIFT_CODE[s.shift_type] || s.shift_type);
        const partialCodes = required
          .filter((s) => !s.is_covered && s.is_partial)
          .map((s) => SHIFT_CODE[s.shift_type] || s.shift_type);
        if (openCodes.length === 0 && partialCodes.length === 0) return '<td class="cell-no-gap">—</td>';
        const openLines = openCodes.map((c) => `<div>OPEN ${escapeHtml(c)}</div>`).join('');
        const partialLines = partialCodes
          .map((c) => `<div class="partial-line">PARTIAL ${escapeHtml(c)}</div>`)
          .join('');
        return `<td class="cell-open">${openLines}${partialLines}</td>`;
      }).join('');

      return `
        <table class="schedule-grid">
          <thead>
            <tr>
              <th class="staff-head">Staff</th>
              ${headerCells}
            </tr>
          </thead>
          <tbody>
            ${staffRows}
            <tr class="open-row">
              <td class="staff-name open-label">OPEN Shifts</td>
              ${openCells}
            </tr>
          </tbody>
        </table>`;
    };

    const tablesHtml = weeks.map((w, idx) => `
      <section class="week-page${idx > 0 ? ' page-break' : ''}">
        <header class="print-header">
          <h1>ALIGN 3.1 Supervised Living</h1>
          <div class="range">${escapeHtml(dateRangeLabel)}</div>
        </header>
        ${buildWeekTable(w)}
        <footer class="print-footer">
          <div>Prepared by ALIGN Integrated Care</div>
          <div>${escapeHtml(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))}</div>
        </footer>
      </section>`).join('');

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>ALIGN 3.1 Weekly Schedule — ${escapeHtml(dateRangeLabel)}</title>
<style>
  @page { size: landscape; margin: 0.4in; }
  * { box-sizing: border-box; }
  html, body {
    font-family: Helvetica, Arial, sans-serif;
    color: #000;
    background: #fff;
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .week-page {
    display: flex;
    flex-direction: column;
    min-height: 7.2in;
  }
  .page-break { page-break-before: always; }

  .print-header {
    text-align: center;
    border-bottom: 2px solid #000;
    padding-bottom: 8px;
    margin-bottom: 12px;
  }
  .print-header h1 {
    font-size: 20pt;
    margin: 0;
    letter-spacing: 0.5px;
  }
  .print-header .range {
    font-size: 13pt;
    margin-top: 4px;
    font-weight: bold;
  }

  table.schedule-grid {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  table.schedule-grid th,
  table.schedule-grid td {
    border: 1px solid #555;
    padding: 8px 4px;
    text-align: center;
    vertical-align: middle;
    font-size: 12pt;
    word-wrap: break-word;
    overflow: hidden;
  }
  table.schedule-grid thead th {
    background: #2c3e50;
    color: #fff;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 8px 4px;
  }
  table.schedule-grid thead .day-name { font-size: 10pt; }
  table.schedule-grid thead .day-date { font-size: 9pt; font-weight: normal; opacity: 0.85; margin-top: 2px; }
  th.staff-head { width: 16%; text-align: left; padding-left: 12px; }

  td.staff-name {
    text-align: left;
    padding-left: 12px;
    font-weight: bold;
    font-size: 12pt;
    background: #ecf0f1;
  }
  /* Alternating staff row backgrounds */
  tr.row-even td:not(.staff-name) { background: #fefefe; }
  tr.row-odd  td:not(.staff-name) { background: #f4f7fb; }
  tr.row-odd  td.staff-name { background: #dfe6ed; }

  td.cell-shift { font-weight: bold; font-size: 13pt; }
  td.cell-shift > div { line-height: 1.25; }
  /* Partner name on double-staffed shifts — matches the on-screen "+ Name"
     hint beneath the shift code. Smaller + lighter so the shift code stays
     dominant; still bolded enough to read on a printed page. */
  td.cell-shift .partner-name {
    font-size: 8pt;
    font-weight: 600;
    font-style: italic;
    color: #555;
    line-height: 1.1;
    margin-top: 1px;
  }
  td.cell-shift .double-badge {
    display: inline-block;
    margin-top: 4px;
    font-size: 8pt;
    font-weight: bold;
    letter-spacing: 1px;
    background: #c62828;
    color: #fff;
    padding: 1px 6px;
    border-radius: 3px;
  }
  /* Manual (custom) shift block — amber styled, sits below standard shifts */
  td.cell-shift .manual-shift {
    margin-top: 6px;
    padding: 3px 4px;
    background: #fff3e0;
    border: 1px solid #d97706;
    border-radius: 3px;
    text-align: center;
  }
  td.cell-shift .manual-label {
    font-size: 9pt;
    font-weight: bold;
    color: #78350f;
    line-height: 1.15;
  }
  td.cell-shift .manual-time {
    font-size: 8pt;
    font-weight: 600;
    color: #78350f;
    line-height: 1.15;
    margin-top: 1px;
  }
  td.cell-shift .manual-partner {
    font-size: 7.5pt;
    font-style: italic;
    color: #78350f;
    margin-top: 1px;
  }
  td.cell-shift .manual-notes {
    font-size: 7.5pt;
    font-style: italic;
    color: #555;
    margin-top: 1px;
    line-height: 1.15;
  }
  td.cell-off { color: #888; font-style: italic; }
  td.cell-empty { color: #aaa; }

  /* OPEN summary row at the bottom of each weekly table */
  tr.open-row td { background: #fff7d6; }
  tr.open-row td.open-label { background: #ffe8a3; color: #7a5a00; }
  tr.open-row td.cell-open {
    color: #b30000;
    font-weight: bold;
    font-size: 11pt;
    line-height: 1.3;
  }
  tr.open-row td.cell-no-gap { color: #2e7d32; }
  tr.open-row td.cell-open .partial-line {
    color: #b35900;
    font-weight: bold;
  }

  .print-footer {
    margin-top: 14px;
    padding-top: 6px;
    border-top: 1px solid #000;
    font-size: 9pt;
    display: flex;
    justify-content: space-between;
    color: #333;
  }
</style>
</head>
<body>
  ${tablesHtml || '<p style="padding:1in">No schedule data available.</p>'}
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 250);
    });
  </script>
</body>
</html>`;
  };

  // Open a popup window with the printable HTML and trigger print
  const openPrintWindow = (dateRangeLabel, days, manualList = []) => {
    const html = buildPrintHTML(dateRangeLabel, days, manualList);
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) {
      alert('Pop-up blocked. Please allow pop-ups for this site to print.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // Build CSV rows for a given list of daily_coverage records + manual shifts.
  // Columns: Date | Day | Shift Type | Shift Label | Start | End | Primary | Secondary | Notes
  // Standard shifts use their canonical label/time; manual shifts use the
  // admin-entered label and free-form times.
  const buildScheduleCSVRows = (days, manualList = []) => {
    const rows = [[
      'Date', 'Day', 'Shift Type', 'Shift Label',
      'Start Time', 'End Time', 'Primary Staff', 'Secondary Staff', 'Notes',
    ]];

    // Index of manual shifts by date so we can emit them immediately after
    // the standard rows for that same date.
    const manualByDate = {};
    (manualList || []).forEach((m) => {
      (manualByDate[m.date] = manualByDate[m.date] || []).push(m);
    });

    days.forEach((day) => {
      const dt = new Date(day.date + 'T00:00:00');
      const dayName = dt.toLocaleDateString('en-US', { weekday: 'long' });

      // Per-day double-shift detection (used as a Notes prefix).
      const shiftsByName = {};
      day.shifts.forEach((s) => {
        if (s.assigned_rss_name) {
          shiftsByName[s.assigned_rss_name] = (shiftsByName[s.assigned_rss_name] || 0) + 1;
        }
        if (s.secondary_rss_name) {
          shiftsByName[s.secondary_rss_name] = (shiftsByName[s.secondary_rss_name] || 0) + 1;
        }
      });

      // Standard shifts in canonical order
      const ordered = SHIFT_DISPLAY_ORDER
        .map((t) => day.shifts.find((s) => s.shift_type === t))
        .filter(Boolean);
      ordered.forEach((shift) => {
        const meta = SHIFT_LABELS[shift.shift_type] || { name: shift.shift_type, time: '' };
        const [start, end] = (meta.time || '').split('–').map((s) => (s || '').trim());
        const primary = shift.assigned_rss_name || (shift.is_covered ? '' : 'UNCOVERED');
        const secondary = shift.secondary_rss_name || '';
        const isDouble = [shift.assigned_rss_name, shift.secondary_rss_name]
          .filter(Boolean)
          .some((n) => (shiftsByName[n] || 0) >= 2);
        const noteBits = [];
        if (isDouble) noteBits.push('DOUBLE SHIFT');
        if (shift.notes) noteBits.push(shift.notes);
        rows.push([
          day.date,
          dayName,
          meta.name + (meta.optional ? ' (Optional)' : ''),
          meta.name,
          start || '',
          end || '',
          primary,
          secondary,
          noteBits.join(' · '),
        ]);
      });

      // Manual shifts for this date, in the order they were created/loaded.
      (manualByDate[day.date] || []).forEach((m) => {
        rows.push([
          m.date,
          dayName,
          'Manual',
          m.label || '',
          m.start_time || '',
          m.end_time || '',
          m.assigned_rss_name || '',
          m.secondary_rss_name || '',
          m.notes || '',
        ]);
      });
    });
    return rows;
  };

  const csvEscape = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const downloadCSV = (rows, filename) => {
    const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  // Returns a flat, deduped list of manual shifts for the CURRENT week. The
  // state index keys manual shifts under both primary and secondary member
  // ids, so we dedupe by `id` here before exporting/printing.
  const currentWeekManualShifts = () => {
    const seen = new Set();
    const flat = [];
    Object.values(manualShifts).forEach((list) => {
      (list || []).forEach((m) => {
        if (m && m.id && !seen.has(m.id)) {
          seen.add(m.id);
          flat.push(m);
        }
      });
    });
    flat.sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || '').localeCompare(b.start_time || ''));
    return flat;
  };

  // ----- Action handlers -----
  const handlePrintWeek = () => {
    if (!weeklyData) return;
    openPrintWindow(formatWeekRange(), weeklyData.daily_coverage, currentWeekManualShifts());
  };

  const handlePrintMonth = async () => {
    try {
      const { days, manualShifts: monthManual } = await fetchMonthCoverage(currentWeekStart);
      const monthLabel = currentWeekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      openPrintWindow(monthLabel, days, monthManual);
    } catch (err) {
      console.error('Error loading month for print:', err);
      alert('Failed to load monthly schedule.');
    }
  };

  const handleExportWeekCSV = () => {
    if (!weeklyData) return;
    const rows = buildScheduleCSVRows(weeklyData.daily_coverage, currentWeekManualShifts());
    downloadCSV(rows, `align-schedule-week-${weeklyData.week_start}.csv`);
  };

  const handleExportMonthCSV = async () => {
    try {
      const { days, manualShifts: monthManual } = await fetchMonthCoverage(currentWeekStart);
      const yyyymm = `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth() + 1).padStart(2, '0')}`;
      downloadCSV(buildScheduleCSVRows(days, monthManual), `align-schedule-month-${yyyymm}.csv`);
    } catch (err) {
      console.error('Error loading month for CSV:', err);
      alert('Failed to load monthly schedule.');
    }
  };

  if (loading && !weeklyData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Schedule</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage 3.1 program shifts: Day, Swing, Evening, Overnight
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-button">
            Today
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="print-menu-button">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background w-56">
              <DropdownMenuLabel>Print Schedule</DropdownMenuLabel>
              <DropdownMenuItem onClick={handlePrintWeek} data-testid="print-week">
                <Printer className="h-4 w-4 mr-2" />
                Print This Week
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handlePrintMonth} data-testid="print-month">
                <Printer className="h-4 w-4 mr-2" />
                Print This Month
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Export Schedule as CSV</DropdownMenuLabel>
              <DropdownMenuItem onClick={handleExportWeekCSV} data-testid="export-week-csv">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export This Week (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportMonthCSV} data-testid="export-month-csv">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export This Month (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Week Navigation & Fill Rate */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={previousWeek} data-testid="prev-week-button">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center min-w-[200px]">
              <div className="text-sm font-medium" data-testid="week-range">
                {formatWeekRange()}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={nextWeek} data-testid="next-week-button">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Weekly Fill Rate */}
          {weeklyData && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Weekly Fill Rate</div>
                <div className="text-2xl font-bold" data-testid="weekly-fill-rate">
                  {weeklyData.average_fill_rate}%
                </div>
              </div>
              <div className="flex items-center gap-1">
                {weeklyData.average_fill_rate >= 75 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : weeklyData.average_fill_rate >= 50 ? (
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Staff × Day Grid */}
      {weeklyData && (
        <Card className="p-0 overflow-x-auto" data-testid="staff-day-grid">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-muted/40">
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wide border-b border-r min-w-[180px] sticky left-0 bg-muted/40 z-10">
                  Staff
                </th>
                <th className="p-2 font-semibold text-xs uppercase tracking-wide border-b border-r min-w-[80px]">
                  Hours
                </th>
                {weeklyData.daily_coverage.map((day, idx) => {
                  const isToday = formatDate(new Date()) === day.date;
                  const dt = new Date(day.date + 'T00:00:00');
                  return (
                    <th
                      key={day.date}
                      className={`p-2 text-xs font-semibold border-b ${isToday ? 'bg-primary/10' : ''}`}
                      data-testid={`grid-header-${idx}`}
                    >
                      <div className="uppercase tracking-wide">{weekDays[idx]}</div>
                      <div className="text-[11px] text-muted-foreground font-normal mt-0.5">
                        {dt.getMonth() + 1}/{dt.getDate()}
                      </div>
                      <div className={`text-[10px] font-bold mt-1 ${
                        day.fill_rate >= 100 ? 'text-green-600'
                          : day.fill_rate >= 66 ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}>
                        {day.fill_rate}%
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {orderedRoster.map((member, rowIdx) => {
                const colors = STAFF_COLORS[member.name] || STAFF_COLORS._default;
                // Standard shifts contribute 8h each; manual shifts contribute
                // their computed duration so the weekly tally reflects custom
                // shifts too.
                const standardHours = weeklyData.daily_coverage.reduce((sum, day) => {
                  const myShiftCount = day.shifts.filter(
                    (s) =>
                      s.assigned_rss_id === member.id
                      || s.assigned_rss_name === member.name
                      || s.secondary_rss_id === member.id
                      || s.secondary_rss_name === member.name
                  ).length;
                  return sum + myShiftCount * 8;
                }, 0);
                const manualHoursTotal = Object.entries(manualShifts).reduce((sum, [k, list]) => {
                  if (!k.endsWith(`|${member.id}`)) return sum;
                  return sum + list.reduce((s, m) => s + computeManualHours(m.start_time, m.end_time), 0);
                }, 0);
                const weekHours = Math.round((standardHours + manualHoursTotal) * 10) / 10;
                const maxHours = (member.staff_type || '').toLowerCase() === 'ft' ? 40 : 30;
                let hoursColorCls;
                if (weekHours > maxHours) {
                  hoursColorCls = 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30';
                } else if (weekHours >= maxHours - 4) {
                  hoursColorCls = 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30';
                } else {
                  hoursColorCls = 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30';
                }
                return (
                  <tr key={member.id} data-testid={`grid-row-${member.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <td
                      className="p-3 border-r border-b sticky left-0 z-10"
                      style={{ background: colors.name }}
                    >
                      <div className="flex items-center gap-2">
                        {/* Reorder controls — admin only. Stacked tiny
                            up/down arrows on the left of the name. */}
                        {isAdmin && (
                          <div
                            className="flex flex-col"
                            data-testid={`reorder-controls-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); moveMember(member.id, 'up'); }}
                              disabled={rowIdx === 0}
                              className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`move-up-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                              title="Move up"
                              aria-label={`Move ${member.name} up`}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); moveMember(member.id, 'down'); }}
                              disabled={rowIdx === orderedRoster.length - 1}
                              className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`move-down-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                              title="Move down"
                              aria-label={`Move ${member.name} down`}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          {/* For admins, the name is a clickable dropdown
                              trigger with Move-to-Top / Move-to-Bottom
                              shortcuts. For staff, plain text. */}
                          {isAdmin ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="text-left hover:underline focus:outline-none"
                                  data-testid={`name-menu-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  <div className="font-semibold text-sm" style={{ color: colors.text }}>
                                    {member.name}
                                  </div>
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuLabel className="text-xs">Reorder</DropdownMenuLabel>
                                <DropdownMenuItem
                                  disabled={rowIdx === 0}
                                  onClick={() => moveMember(member.id, 'top')}
                                  data-testid={`move-top-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  <ChevronsUp className="h-4 w-4 mr-2" />
                                  Move to Top
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={rowIdx === orderedRoster.length - 1}
                                  onClick={() => moveMember(member.id, 'bottom')}
                                  data-testid={`move-bottom-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  <ChevronsDown className="h-4 w-4 mr-2" />
                                  Move to Bottom
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <div className="font-semibold text-sm" style={{ color: colors.text }}>
                              {member.name}
                            </div>
                          )}
                          <div className="text-[10px] uppercase tracking-wide opacity-75" style={{ color: colors.text }}>
                            {member.staff_type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className={`p-2 border-r border-b text-center font-bold text-sm ${hoursColorCls}`}
                      data-testid={`hours-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                      title={`${weekHours} of ${maxHours}h max`}
                    >
                      {weekHours}h
                      <div className="text-[9px] font-normal opacity-75 mt-0.5">
                        / {maxHours}h
                      </div>
                    </td>
                    {weeklyData.daily_coverage.map((day, dayIdx) => {
                      // Pick up shifts where this member is EITHER primary or
                      // secondary, so double-staffed slots show on both rows.
                      const myShifts = day.shifts.filter(
                        (s) =>
                          s.assigned_rss_id === member.id
                          || s.assigned_rss_name === member.name
                          || s.secondary_rss_id === member.id
                          || s.secondary_rss_name === member.name
                      );
                      const isDouble = myShifts.length >= 2;
                      const annotation = annotations[`${day.date}|${member.id}`];
                      const annStyle = annotation ? ANNOTATION_STYLE[annotation.shift_type] : null;
                      const myManualShifts = manualShifts[`${day.date}|${member.id}`] || [];
                      const hasAny = myShifts.length > 0 || annotation || myManualShifts.length > 0;
                      // Manual-shift pills used in multiple branches below.
                      const manualPills = myManualShifts.map((m) => {
                        const isSecondaryHere = m.secondary_rss_id === member.id;
                        const partnerName = isSecondaryHere ? m.assigned_rss_name : m.secondary_rss_name;
                        // Does this manual shift fully cover at least one canonical
                        // window? If yes, the backend has already set that slot's
                        // `is_covered/covered_by_manual=true`, so we surface a tiny
                        // "Counts as Coverage" check to make the link visible.
                        const coverageSlots = manualShiftCoverageSlots(m);
                        return (
                          <div
                            key={m.id}
                            className="flex flex-col items-center"
                            data-testid={`manual-pill-${member.name.replace(/\s+/g, '-').toLowerCase()}-${dayIdx}`}
                          >
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border"
                              style={{
                                background: MANUAL_STYLE.pillBg,
                                borderColor: MANUAL_STYLE.border,
                                color: '#fff',
                              }}
                              title={`${m.label}\n${m.start_time} – ${m.end_time}`}
                            >
                              <Sparkles className="h-3 w-3" />
                              {m.label}
                            </span>
                            <span className="text-[10px] font-semibold mt-0.5"
                                  style={{ color: MANUAL_STYLE.text }}>
                              {m.start_time}–{m.end_time}
                            </span>
                            {partnerName && (
                              <span className="text-[10px] italic text-muted-foreground">
                                + {partnerName}
                              </span>
                            )}
                            {coverageSlots.length > 0 && (
                              <span
                                className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0 rounded text-[9px] font-bold bg-green-600 text-white"
                                title={`Counts as ${coverageSlots.map((s) => SHIFT_CODE[s] || s).join(' / ')} coverage`}
                                data-testid={`manual-coverage-badge-${m.id}`}
                              >
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                Counts as Coverage
                              </span>
                            )}
                          </div>
                        );
                      });
                      return (
                        <td
                          key={day.date}
                          className={`p-2 border-b text-center ${isAdmin ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} transition-opacity relative`}
                          style={{ background: colors.row }}
                          onClick={() => { if (isAdmin) openCellDialog(member, day, myShifts); }}
                          data-testid={`cell-${member.name.replace(/\s+/g, '-').toLowerCase()}-${dayIdx}`}
                        >
                          {annotation && annStyle ? (
                            <div className="flex flex-col items-center gap-1">
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
                                style={{
                                  background: annStyle.bg,
                                  borderColor: annStyle.border,
                                  color: annStyle.text,
                                }}
                                data-testid={`annotation-pill-${annotation.shift_type}-${dayIdx}`}
                              >
                                {annotation.shift_type === 'requested_off' && <CalendarX className="h-3 w-3" />}
                                {annotation.shift_type === 'needs_cover' && <UserX className="h-3 w-3" />}
                                {annotation.shift_type === 'swap_pending' && <ArrowLeftRight className="h-3 w-3" />}
                                {annotation.shift_type === 'swap_pending'
                                  ? `Swap: ${annotation.swap_with || '—'}`
                                  : annStyle.label}
                              </span>
                              {/* Show the original shift in muted text so admin
                                  can see what's being affected */}
                              {annotation.original_shift && (
                                <span className="text-[10px] text-muted-foreground">
                                  was {SHIFT_CODE[annotation.original_shift] || annotation.original_shift}
                                </span>
                              )}
                              {/* Manual shifts coexist with annotations */}
                              {manualPills}
                            </div>
                          ) : !hasAny ? (
                            <span className="text-xs text-muted-foreground italic">Off</span>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              {SHIFT_DISPLAY_ORDER
                                .map((t) => myShifts.find((s) => s.shift_type === t))
                                .filter(Boolean)
                                .map((s) => {
                                  // Name of the partner on this shift (if any),
                                  // i.e., the OTHER staff member if this member
                                  // is currently primary or secondary on it.
                                  let partnerName = null;
                                  if (s.assigned_rss_id === member.id || s.assigned_rss_name === member.name) {
                                    partnerName = s.secondary_rss_name || null;
                                  } else if (s.secondary_rss_id === member.id || s.secondary_rss_name === member.name) {
                                    partnerName = s.assigned_rss_name || null;
                                  }
                                  // Completed shift switch for THIS shift type
                                  // on THIS cell, if any. Renders a distinct
                                  // violet "Switched: A → B" pill underneath
                                  // the shift code so the original RSS is
                                  // still visible after the reassignment.
                                  const swap = completedSwaps[`${day.date}|${member.id}|${s.shift_type}`];
                                  return (
                                    <React.Fragment key={s.id || s.shift_type}>
                                      <span
                                        className="inline-block px-2.5 py-1 rounded-full text-xs font-bold"
                                        style={{ background: colors.text, color: '#fff' }}
                                      >
                                        {SHIFT_CODE[s.shift_type]}
                                      </span>
                                      {partnerName && (
                                        <span
                                          className="text-[10px] text-muted-foreground italic"
                                          data-testid={`partner-${member.name.replace(/\s+/g, '-').toLowerCase()}-${dayIdx}`}
                                        >
                                          + {partnerName}
                                        </span>
                                      )}
                                      {swap && (
                                        <span
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                                          style={{
                                            background: ANNOTATION_STYLE.completed_swap.bg,
                                            borderColor: ANNOTATION_STYLE.completed_swap.border,
                                            color: ANNOTATION_STYLE.completed_swap.text,
                                          }}
                                          title={`Originally assigned to ${swap.original_member_name}`}
                                          data-testid={`switched-pill-${member.name.replace(/\s+/g, '-').toLowerCase()}-${dayIdx}`}
                                        >
                                          <Repeat className="h-3 w-3" />
                                          Switched: {swap.original_member_name} → {member.name}
                                        </span>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              {isDouble && (
                                <span
                                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600 text-white"
                                  data-testid={`double-badge-${member.name.replace(/\s+/g, '-').toLowerCase()}-${dayIdx}`}
                                >
                                  2x Double
                                </span>
                              )}
                              {manualPills}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {/* OPEN row — uncovered required shifts highlighted.
                  Hidden entirely when every required slot across the whole
                  week is filled (no OPEN, no PARTIAL on any day). */}
              {(() => {
                const weekHasGaps = weeklyData.daily_coverage.some((day) => {
                  const reqs = day.shifts.filter(
                    (s) => s.shift_type === 'day' || s.shift_type === 'evening' || s.shift_type === 'overnight'
                  );
                  return reqs.some((s) => !s.is_covered);
                });
                if (!weekHasGaps) return null;
                return (
              <tr className="bg-amber-50 dark:bg-amber-950/30" data-testid="open-shifts-row">
                <td className="p-3 border-r border-t border-b font-bold text-amber-900 dark:text-amber-200 sticky left-0 bg-amber-50 dark:bg-amber-950/30 z-10">
                  OPEN Shifts
                </td>
                <td className="border-r border-t border-b" aria-hidden="true" />
                {weeklyData.daily_coverage.map((day, dayIdx) => {
                  // Required slots that have NO coverage at all (no standard
                  // assignment, no fully-covering manual). Slots that are
                  // partially covered by a manual shift are surfaced as a
                  // softer "Partial" pill rather than the loud red OPEN pill.
                  const reqs = day.shifts.filter(
                    (s) => s.shift_type === 'day' || s.shift_type === 'evening' || s.shift_type === 'overnight'
                  );
                  const openShifts = reqs.filter((s) => !s.is_covered && !s.is_partial);
                  const partialShifts = reqs.filter((s) => !s.is_covered && s.is_partial);
                  const allOk = openShifts.length === 0 && partialShifts.length === 0;
                  return (
                    <td key={day.date} className="p-2 border-t border-b text-center align-top" data-testid={`open-row-${dayIdx}`}>
                      {allOk ? (
                        <span className="text-xs text-green-700 dark:text-green-400">—</span>
                      ) : (
                        <div className="flex flex-col gap-1 items-center">
                          {openShifts.map((s) => (
                            <span
                              key={s.shift_type}
                              className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white tracking-wide"
                              data-testid={`open-pill-${s.shift_type}-${dayIdx}`}
                            >
                              OPEN {SHIFT_CODE[s.shift_type]}
                            </span>
                          ))}
                          {partialShifts.map((s) => (
                            <span
                              key={`partial-${s.shift_type}`}
                              className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-orange-400 text-orange-950 tracking-wide"
                              title="A manual shift partially overlaps this slot but doesn't fully cover the standard window."
                              data-testid={`partial-pill-${s.shift_type}-${dayIdx}`}
                            >
                              PARTIAL {SHIFT_CODE[s.shift_type]}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
                );
              })()}
            </tbody>
          </table>

          {/* Legend */}
          <div className="px-4 py-3 border-t bg-muted/20 flex flex-wrap gap-3 text-xs items-center">
            <span className="font-semibold text-muted-foreground mr-2">Shift codes:</span>
            <span className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-blue-700 text-white font-bold text-[10px]">7a-3p</span> Day
            </span>
            <span className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-amber-700 text-white font-bold text-[10px]">11a-7p</span> Swing (Optional)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-purple-700 text-white font-bold text-[10px]">3p-11p</span> Evening
            </span>
            <span className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-indigo-700 text-white font-bold text-[10px]">11p-7a</span> Overnight
            </span>
            <span className="flex items-center gap-1.5 ml-2 pl-2 border-l">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                style={{
                  background: ANNOTATION_STYLE.completed_swap.bg,
                  borderColor: ANNOTATION_STYLE.completed_swap.border,
                  color: ANNOTATION_STYLE.completed_swap.text,
                }}
              >
                <Repeat className="h-3 w-3" />
                Switched
              </span>
              Completed RSS swap
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
                style={{ background: MANUAL_STYLE.pillBg, borderColor: MANUAL_STYLE.border, color: '#fff' }}
              >
                <Sparkles className="h-3 w-3" />
                Manual
              </span>
              Custom shift
            </span>
          </div>
        </Card>
      )}

      {/* Assign Cell Dialog: pick a shift for {member} on {date} */}
      <Dialog open={isCellDialogOpen} onOpenChange={setIsCellDialogOpen}>
        {/* Flex column with max-h-90vh + p-0 overrides the default grid/padding
            so we can split the dialog into [fixed header] + [scrollable body]
            + [sticky footer]. Without this the modal grew too tall on laptop
            screens once Secondary / Admin / Manual sections were added. */}
        <DialogContent
          className="sm:max-w-[480px] max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
          data-testid="cell-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Assign Shift</DialogTitle>
            <DialogDescription>
              {cellContext && (
                <>
                  <span className="font-semibold">{cellContext.member.name}</span>
                  {' on '}
                  <span className="font-semibold">
                    {new Date(cellContext.day.date + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'long', month: 'long', day: 'numeric'
                    })}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable body — flex-1 takes remaining height; overflow-y-auto
              gives us the inner scroll. Extra bottom padding keeps the last
              field visible above the sticky footer. */}
          <div
            className="flex-1 overflow-y-auto px-6 pb-4"
            data-testid="cell-dialog-body"
          >

          <div className="grid grid-cols-2 gap-3 py-4">
            {SHIFT_CHOICES.map((c) => {
              const isCurrent = cellContext?.currentShifts?.some((s) => s.shift_type === c.type);
              return (
                <button
                  key={c.type}
                  type="button"
                  onClick={() => handleCellAssign(c.type)}
                  className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                    isCurrent ? 'border-primary bg-primary/10' : 'border-border bg-background'
                  }`}
                  data-testid={`cell-shift-${c.type}`}
                >
                  <div className="text-xs uppercase tracking-wide font-semibold mb-1">{c.label}</div>
                  <div className="text-lg font-bold">{c.code}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{c.time}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {c.optional && (
                      <Badge variant="secondary" className="text-[10px]">Optional</Badge>
                    )}
                    {isCurrent && (
                      <Badge variant="default" className="text-[10px]">Assigned</Badge>
                    )}
                    {(() => {
                      const st = cellShiftAvailability(c.type);
                      if (!st) return null;
                      const style = {
                        preferred: { background: '#D7F2E7', color: '#116649', label: 'Preferred' },
                        available: { background: '#DCEDFB', color: '#1E5A8E', label: 'Available' },
                        unavailable: { background: '#FBE1E1', color: '#912F2F', label: 'Unavailable' },
                        no_response: { background: '#F1F1F3', color: '#6B6B72', label: 'No Response' },
                      }[st];
                      return (
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ background: style.background, color: style.color }}
                          data-testid={`cell-availability-${c.type}`}
                        >
                          {style.label}
                        </span>
                      );
                    })()}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Availability restrictions / notes from the staff member's submission */}
          {cellAvailability?.found && (cellAvailability.restriction || cellAvailability.note || cellAvailability.general_notes) && (
            <div className="rounded-md border border-[#D99E22]/50 bg-[#FDF0D2]/50 p-2.5 mb-3 text-xs space-y-0.5" data-testid="cell-availability-restrictions">
              <div className="font-semibold text-[#8A6210]">Staff availability notes ({cellAvailability.period_name})</div>
              {cellAvailability.restriction && <div className="text-[#8A6210]">Restriction: {cellAvailability.restriction}</div>}
              {cellAvailability.note && <div className="text-[#8A6210]">Day note: {cellAvailability.note}</div>}
              {cellAvailability.general_notes && <div className="text-[#8A6210]">Period notes: {cellAvailability.general_notes}</div>}
            </div>
          )}

          {/* ===== SECONDARY STAFF (working-shift only) =====
              Shown only when this member is currently the PRIMARY on a
              working shift. Lets admin pair another teammate on the same
              shift, or remove an existing pairing. Hidden for "Off" cells. */}
          {cellContext && (() => {
            const primaryShifts = (cellContext.currentShifts || []).filter(
              (s) =>
                s.assigned_rss_id === cellContext.member.id
                || s.assigned_rss_name === cellContext.member.name
            );
            if (primaryShifts.length === 0) return null;
            // The feature targets one shift at a time. If this person is doing
            // a double-shift day (e.g. day + evening), we only attach a
            // secondary to the FIRST one; admins can re-open the modal after.
            const targetShift = primaryShifts[0];
            const hasSecondary = !!targetShift.secondary_rss_id;
            return (
              <div className="border-t pt-3 space-y-2" data-testid="secondary-section">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Secondary staff (optional) · {SHIFT_CODE[targetShift.shift_type]}
                </div>
                {hasSecondary ? (
                  <div className="flex items-center justify-between rounded-md border p-2"
                       data-testid="secondary-existing">
                    <span className="text-sm">
                      Sharing with <span className="font-semibold">{targetShift.secondary_rss_name}</span>
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveSecondary(targetShift)}
                      data-testid="secondary-remove"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      value={secondaryPickerId}
                      onChange={(e) => setSecondaryPickerId(e.target.value)}
                      className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                      data-testid="secondary-select"
                    >
                      <option value="">Select secondary staff…</option>
                      {teamMembers
                        .filter((m) => m.id !== cellContext.member.id && (m.is_active ?? true))
                        .map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </select>
                    <Button
                      size="sm"
                      onClick={() => handleAddSecondary(targetShift, secondaryPickerId)}
                      disabled={!secondaryPickerId}
                      data-testid="secondary-add"
                    >
                      Add as Secondary
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ===== ADMIN ANNOTATION OPTIONS ===== */}
          {isAdmin && cellContext && (() => {
            const existing = annotations[`${cellContext.day.date}|${cellContext.member.id}`];
            const hasShifts = cellContext.currentShifts.length > 0;
            // Completed swaps for any of this cell's shift types
            const cellSwaps = (cellContext.currentShifts || [])
              .map((s) => completedSwaps[`${cellContext.day.date}|${cellContext.member.id}|${s.shift_type}`])
              .filter(Boolean);
            // Filter the primary shifts list (this member actually holds it as
            // primary) — used to seed the "Record Completed Switch" form.
            const primaryShifts = (cellContext.currentShifts || []).filter(
              (s) => s.assigned_rss_id === cellContext.member.id || s.assigned_rss_name === cellContext.member.name
            );
            return (
              <div
                className="border-t pt-3 space-y-3"
                data-testid="annotation-section"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Admin actions
                  <Badge variant="outline" className="text-[9px]">Admin only</Badge>
                </div>

                {/* ===== COMPLETED SWITCH HISTORY (read-only details) ===== */}
                {/* Surfaces every completed_swap stored against THIS cell. Coexists
                    with operational annotations (requested_off / needs_cover /
                    swap_pending) below. */}
                {cellSwaps.length > 0 && cellSwaps.map((swap) => (
                  <div
                    key={swap.id}
                    className="rounded-md border p-3 space-y-2"
                    style={{
                      background: ANNOTATION_STYLE.completed_swap.bg,
                      borderColor: ANNOTATION_STYLE.completed_swap.border,
                    }}
                    data-testid={`completed-swap-details-${swap.original_shift}`}
                  >
                    <div
                      className="flex items-center gap-2 text-sm font-semibold"
                      style={{ color: ANNOTATION_STYLE.completed_swap.text }}
                    >
                      <Repeat className="h-4 w-4" />
                      Shift Switch — Completed
                    </div>
                    <div className="text-xs space-y-1" style={{ color: ANNOTATION_STYLE.completed_swap.text }}>
                      <p><span className="opacity-75">Original RSS:</span> <span className="font-semibold">{swap.original_member_name}</span></p>
                      <p><span className="opacity-75">Picked up by:</span> <span className="font-semibold">{swap.member_name}</span></p>
                      <p><span className="opacity-75">Shift:</span> <span className="font-semibold">{SHIFT_CODE[swap.original_shift] || swap.original_shift}</span></p>
                      <p><span className="opacity-75">Status:</span> <span className="font-semibold">Completed</span></p>
                      {swap.notes && (
                        <p className="italic"><span className="opacity-75">Notes:</span> {swap.notes}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleClearCompletedSwap(swap.id)}
                        data-testid={`completed-swap-clear-${swap.original_shift}`}
                      >
                        Clear switch annotation only
                      </Button>
                    </div>
                  </div>
                ))}

                {/* If an annotation already exists, show a status row with
                    Clear / Confirm-Swap actions. Otherwise show the 3 buttons. */}
                {existing ? (
                  <div
                    className="rounded-md border p-3 space-y-2"
                    style={{
                      background: ANNOTATION_STYLE[existing.shift_type]?.bg,
                      borderColor: ANNOTATION_STYLE[existing.shift_type]?.border,
                    }}
                    data-testid="annotation-existing"
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold"
                         style={{ color: ANNOTATION_STYLE[existing.shift_type]?.text }}>
                      {existing.shift_type === 'requested_off' && <CalendarX className="h-4 w-4" />}
                      {existing.shift_type === 'needs_cover' && <UserX className="h-4 w-4" />}
                      {existing.shift_type === 'swap_pending' && <ArrowLeftRight className="h-4 w-4" />}
                      {existing.shift_type === 'requested_off' && 'Requested Off'}
                      {existing.shift_type === 'needs_cover' && 'Needs Cover'}
                      {existing.shift_type === 'swap_pending' && `Swap pending → ${existing.swap_with}`}
                    </div>
                    {existing.original_shift && (
                      <p className="text-xs text-muted-foreground">
                        Original shift: {SHIFT_CODE[existing.original_shift] || existing.original_shift}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleClearAnnotation(existing.id)}
                        data-testid="annotation-clear"
                      >
                        Clear annotation
                      </Button>
                      {existing.shift_type === 'swap_pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleConfirmSwap(existing.id)}
                          data-testid="annotation-confirm-swap"
                        >
                          Confirm Swap
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {/* Requested Off — shown when the cell is Off */}
                    {!hasShifts && (
                      <button
                        type="button"
                        onClick={handleRequestedOff}
                        className="flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left hover:bg-amber-50 dark:hover:bg-amber-950/30"
                        style={{ borderColor: ANNOTATION_STYLE.requested_off.border, color: ANNOTATION_STYLE.requested_off.text }}
                        data-testid="annotation-requested-off"
                      >
                        <CalendarX className="h-4 w-4" style={{ color: ANNOTATION_STYLE.requested_off.border }} />
                        <span className="font-semibold">Requested Off</span>
                        <span className="text-xs text-muted-foreground ml-auto">Mark as time-off request</span>
                      </button>
                    )}
                    {/* Needs Cover — shown when the cell has an assignment */}
                    {hasShifts && (
                      <button
                        type="button"
                        onClick={handleNeedsCover}
                        className="flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left hover:bg-orange-50 dark:hover:bg-orange-950/30"
                        style={{ borderColor: ANNOTATION_STYLE.needs_cover.border, color: ANNOTATION_STYLE.needs_cover.text }}
                        data-testid="annotation-needs-cover"
                      >
                        <UserX className="h-4 w-4" style={{ color: ANNOTATION_STYLE.needs_cover.border }} />
                        <span className="font-semibold">Needs Cover</span>
                        <span className="text-xs text-muted-foreground ml-auto">Flag this shift for re-staffing</span>
                      </button>
                    )}
                    {/* Swap Pending — shown when the cell has an assignment */}
                    {hasShifts && (
                      <div
                        className="rounded-md border p-2"
                        style={{ borderColor: ANNOTATION_STYLE.swap_pending.border }}
                      >
                        <div className="flex items-center gap-2 text-sm mb-2"
                             style={{ color: ANNOTATION_STYLE.swap_pending.text }}>
                          <ArrowLeftRight className="h-4 w-4" style={{ color: ANNOTATION_STYLE.swap_pending.border }} />
                          <span className="font-semibold">Swap Pending</span>
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={swapPartnerId}
                            onChange={(e) => setSwapPartnerId(e.target.value)}
                            className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="annotation-swap-partner-select"
                          >
                            <option value="">Select swap partner…</option>
                            {teamMembers
                              .filter((m) => m.id !== cellContext.member.id && (m.is_active ?? true))
                              .map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                          </select>
                          <Button
                            size="sm"
                            onClick={() => {
                              const partner = teamMembers.find((m) => m.id === swapPartnerId);
                              if (!partner) {
                                alert('Pick a staff member to swap with.');
                                return;
                              }
                              handleProposeSwap(partner);
                            }}
                            disabled={!swapPartnerId}
                            data-testid="annotation-propose-swap"
                          >
                            Propose
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ===== RECORD COMPLETED SHIFT SWITCH ===== */}
                {/* Only useful when this member CURRENTLY holds the shift as
                    primary — i.e., the switch is already done and admin is
                    backfilling the original RSS for accountability. */}
                {primaryShifts.length > 0 && (
                  recordSwitch ? (
                    <div
                      className="rounded-md border p-3 space-y-2"
                      style={{
                        background: ANNOTATION_STYLE.completed_swap.bg,
                        borderColor: ANNOTATION_STYLE.completed_swap.border,
                      }}
                      data-testid="record-switch-form"
                    >
                      <div
                        className="flex items-center gap-2 text-sm font-semibold"
                        style={{ color: ANNOTATION_STYLE.completed_swap.text }}
                      >
                        <History className="h-4 w-4" />
                        Record Completed Shift Switch
                      </div>
                      <p className="text-xs" style={{ color: ANNOTATION_STYLE.completed_swap.text }}>
                        <span className="opacity-75">Picked up by:</span>{' '}
                        <span className="font-semibold">{cellContext.member.name}</span>
                      </p>

                      {/* Shift picker — only if this member holds multiple shifts */}
                      {primaryShifts.length > 1 ? (
                        <div className="space-y-1">
                          <label className="block text-[11px] font-semibold uppercase tracking-wide"
                                 style={{ color: ANNOTATION_STYLE.completed_swap.text }}>
                            Shift
                          </label>
                          <select
                            value={recordSwitch.shiftType || ''}
                            onChange={(e) => setRecordSwitch({ ...recordSwitch, shiftType: e.target.value })}
                            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="record-switch-shift-select"
                          >
                            <option value="">Select shift…</option>
                            {primaryShifts.map((s) => (
                              <option key={s.id || s.shift_type} value={s.shift_type}>
                                {SHIFT_CHOICES.find((c) => c.type === s.shift_type)?.label || s.shift_type}
                                {' · '}
                                {SHIFT_CODE[s.shift_type]}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: ANNOTATION_STYLE.completed_swap.text }}>
                          <span className="opacity-75">Shift:</span>{' '}
                          <span className="font-semibold">
                            {SHIFT_CODE[primaryShifts[0].shift_type] || primaryShifts[0].shift_type}
                          </span>
                        </p>
                      )}

                      <div className="space-y-1">
                        <label className="block text-[11px] font-semibold uppercase tracking-wide"
                               style={{ color: ANNOTATION_STYLE.completed_swap.text }}>
                          Original RSS (who had the shift)
                        </label>
                        <select
                          value={recordSwitch.originalId || ''}
                          onChange={(e) => setRecordSwitch({ ...recordSwitch, originalId: e.target.value })}
                          className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                          data-testid="record-switch-original-select"
                        >
                          <option value="">Select original RSS…</option>
                          {teamMembers
                            .filter((m) => m.id !== cellContext.member.id && (m.is_active ?? true))
                            .map((m) => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="block text-[11px] font-semibold uppercase tracking-wide"
                               style={{ color: ANNOTATION_STYLE.completed_swap.text }}>
                          Notes (optional)
                        </label>
                        <Textarea
                          rows={2}
                          placeholder="Reason, approver, etc."
                          value={recordSwitch.notes || ''}
                          onChange={(e) => setRecordSwitch({ ...recordSwitch, notes: e.target.value })}
                          data-testid="record-switch-notes"
                        />
                      </div>

                      <div className="flex flex-wrap gap-2 justify-end pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRecordSwitch(null)}
                          data-testid="record-switch-cancel"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={submitRecordSwitch}
                          data-testid="record-switch-save"
                        >
                          Save Switch
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRecordSwitch({
                        shiftType: primaryShifts.length === 1 ? primaryShifts[0].shift_type : '',
                        originalId: '',
                        notes: '',
                      })}
                      className="flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left hover:opacity-90 w-full"
                      style={{
                        borderColor: ANNOTATION_STYLE.completed_swap.border,
                        color: ANNOTATION_STYLE.completed_swap.text,
                      }}
                      data-testid="record-switch-button"
                    >
                      <History className="h-4 w-4" style={{ color: ANNOTATION_STYLE.completed_swap.border }} />
                      <span className="font-semibold">Record Completed Shift Switch</span>
                      <span className="text-xs text-muted-foreground ml-auto">Backfill original RSS</span>
                    </button>
                  )
                )}
              </div>
            );
          })()}

          {/* ===== MANUAL SHIFT (admin only) =====
              List of existing manual shifts for this cell + an inline form to
              create a new one or edit an existing one. Stored independently
              from the 4 fixed slots so it can't break fill-rate or coverage. */}
          {isAdmin && cellContext && (() => {
            const cellManualShifts = manualShifts[`${cellContext.day.date}|${cellContext.member.id}`] || [];
            return (
              <div className="border-t pt-3 space-y-3" data-testid="manual-shift-section">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Manual shifts
                    <Badge variant="outline" className="text-[9px]">Custom</Badge>
                  </div>
                  {!manualForm && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setManualForm({
                        id: null,
                        label: '',
                        start_time: '',
                        end_time: '',
                        secondary_rss_id: '',
                        notes: '',
                      })}
                      data-testid="manual-shift-add"
                      style={{ borderColor: MANUAL_STYLE.border, color: MANUAL_STYLE.text }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Manual Shift
                    </Button>
                  )}
                </div>

                {/* Existing manual shifts for this cell */}
                {cellManualShifts.length > 0 && !manualForm && (
                  <div className="space-y-2">
                    {cellManualShifts.map((m) => {
                      const isSecondaryHere = m.secondary_rss_id === cellContext.member.id;
                      const partnerName = isSecondaryHere ? m.assigned_rss_name : m.secondary_rss_name;
                      return (
                        <div
                          key={m.id}
                          className="rounded-md border p-3 flex items-start justify-between gap-3"
                          style={{ background: MANUAL_STYLE.bg, borderColor: MANUAL_STYLE.border }}
                          data-testid={`manual-shift-row-${m.id}`}
                        >
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Sparkles className="h-4 w-4" style={{ color: MANUAL_STYLE.border }} />
                              <span className="font-semibold text-sm" style={{ color: MANUAL_STYLE.text }}>
                                {m.label}
                              </span>
                              {isSecondaryHere && (
                                <Badge variant="secondary" className="text-[9px]">Secondary</Badge>
                              )}
                              {manualShiftCoverageSlots(m).length > 0 && (
                                <Badge
                                  className="text-[9px] bg-green-600 hover:bg-green-600"
                                  data-testid={`manual-coverage-modal-${m.id}`}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Counts as Coverage
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs" style={{ color: MANUAL_STYLE.text }}>
                              {m.start_time} – {m.end_time}
                              {' · '}
                              <span className="opacity-75">{computeManualHours(m.start_time, m.end_time)}h</span>
                            </p>
                            {partnerName && (
                              <p className="text-xs text-muted-foreground italic">
                                Paired with {partnerName}
                              </p>
                            )}
                            {m.notes && (
                              <p className="text-xs text-muted-foreground italic">{m.notes}</p>
                            )}
                          </div>
                          {/* Edit / delete only available from the PRIMARY's cell to keep
                              the source of truth obvious. From the secondary's view it's
                              read-only. */}
                          {!isSecondaryHere && (
                            <div className="flex flex-col gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setManualForm({
                                  id: m.id,
                                  label: m.label || '',
                                  start_time: m.start_time || '',
                                  end_time: m.end_time || '',
                                  secondary_rss_id: m.secondary_rss_id || '',
                                  notes: m.notes || '',
                                })}
                                data-testid={`manual-shift-edit-${m.id}`}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteManualShift(m.id)}
                                data-testid={`manual-shift-delete-${m.id}`}
                                className="h-7 px-2 text-red-700 hover:text-red-800"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Inline create/edit form */}
                {manualForm && (
                  <div
                    className="rounded-md border p-3 space-y-3"
                    style={{ background: MANUAL_STYLE.bg, borderColor: MANUAL_STYLE.border }}
                    data-testid="manual-shift-form"
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: MANUAL_STYLE.text }}>
                      <Sparkles className="h-4 w-4" />
                      {manualForm.id ? 'Edit Manual Shift' : 'New Manual Shift'}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[11px] font-semibold uppercase tracking-wide" style={{ color: MANUAL_STYLE.text }}>
                        Label <span className="text-red-600">*</span>
                      </label>
                      <Input
                        value={manualForm.label}
                        onChange={(e) => setManualForm({ ...manualForm, label: e.target.value })}
                        placeholder="e.g., Training Coverage"
                        data-testid="manual-shift-label"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="block text-[11px] font-semibold uppercase tracking-wide" style={{ color: MANUAL_STYLE.text }}>
                          Start <span className="text-red-600">*</span>
                        </label>
                        <Input
                          value={manualForm.start_time}
                          onChange={(e) => setManualForm({ ...manualForm, start_time: e.target.value })}
                          placeholder="10:00 AM"
                          data-testid="manual-shift-start"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[11px] font-semibold uppercase tracking-wide" style={{ color: MANUAL_STYLE.text }}>
                          End <span className="text-red-600">*</span>
                        </label>
                        <Input
                          value={manualForm.end_time}
                          onChange={(e) => setManualForm({ ...manualForm, end_time: e.target.value })}
                          placeholder="2:00 PM"
                          data-testid="manual-shift-end"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[11px] font-semibold uppercase tracking-wide" style={{ color: MANUAL_STYLE.text }}>
                        Secondary staff (optional)
                      </label>
                      <select
                        value={manualForm.secondary_rss_id}
                        onChange={(e) => setManualForm({ ...manualForm, secondary_rss_id: e.target.value })}
                        className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="manual-shift-secondary"
                      >
                        <option value="">No secondary</option>
                        {teamMembers
                          .filter((m) => m.id !== cellContext.member.id && (m.is_active ?? true))
                          .map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[11px] font-semibold uppercase tracking-wide" style={{ color: MANUAL_STYLE.text }}>
                        Notes (optional)
                      </label>
                      <Textarea
                        rows={2}
                        value={manualForm.notes}
                        onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                        placeholder="Reason, context, etc."
                        data-testid="manual-shift-notes"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setManualForm(null)}
                        data-testid="manual-shift-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={submitManualShift}
                        data-testid="manual-shift-save"
                      >
                        {manualForm.id ? 'Save Changes' : 'Add Shift'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          </div>
          {/* end .flex-1 scrollable body */}

          <DialogFooter className="gap-2 flex-col sm:flex-row px-6 py-4 border-t bg-background shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                const cs = cellContext?.currentShifts?.[0];
                const SHIFT_TIMES = {
                  day:       { start: '7:00 AM',  end: '3:00 PM',  num: '1st' },
                  swing:     { start: '11:00 AM', end: '7:00 PM',  num: '2nd' },
                  evening:   { start: '3:00 PM',  end: '11:00 PM', num: '2nd' },
                  overnight: { start: '11:00 PM', end: '7:00 AM',  num: '3rd' },
                };
                const t = cs ? (SHIFT_TIMES[cs.shift_type] || {}) : {};
                const params = new URLSearchParams({
                  new: '1',
                  date: cellContext?.day?.date || '',
                  startTime: t.start || '',
                  endTime: t.end || '',
                  staffName: cellContext?.member?.name || '',
                  shiftNumber: t.num || '',
                });
                navigate(`/shift-notes?${params.toString()}`);
              }}
              data-testid="log-note-button"
              className="sm:mr-auto"
            >
              <ClipboardList className="h-4 w-4 mr-1" />
              Log Note
            </Button>
            {cellContext?.currentShifts?.length > 0 && (
              <Button
                variant="destructive"
                onClick={handleCellRemove}
                data-testid="cell-remove-button"
              >
                <X className="h-4 w-4 mr-1" />
                Mark Off
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsCellDialogOpen(false)} data-testid="cell-cancel-button">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Double-Shift confirmation dialog */}
      <Dialog open={!!doubleConfirm} onOpenChange={(open) => !open && setDoubleConfirm(null)}>
        <DialogContent className="sm:max-w-[460px]" data-testid="double-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Confirm Double Shift
            </DialogTitle>
            <DialogDescription>
              {doubleConfirm && (
                <>
                  <span className="font-semibold">{doubleConfirm.member.name}</span>
                  {' is already assigned to '}
                  <span className="font-semibold">
                    {doubleConfirm.existingShifts
                      .map((s) => SHIFT_CODE[s.shift_type])
                      .join(' & ')}
                  </span>
                  {' today — assign as double shift ('}
                  <span className="font-semibold">{SHIFT_CODE[doubleConfirm.newShiftType]}</span>
                  {')?'}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3 text-xs text-amber-900 dark:text-amber-200">
            Double shifts will be flagged with a <strong>2x</strong> badge on the schedule
            grid and called out in print and CSV exports for supervisor review.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDoubleConfirm(null)} data-testid="double-cancel">
              Cancel
            </Button>
            <Button onClick={handleConfirmDouble} data-testid="double-confirm-button">
              Confirm Double
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign confirmation — clicking an already-selected shift toggles it off */}
      <Dialog open={!!unassignConfirm} onOpenChange={(open) => !open && setUnassignConfirm(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="unassign-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Remove {cellContext?.member?.name} from this shift?
            </DialogTitle>
            <DialogDescription>
              {unassignConfirm && cellContext && (
                <>
                  {cellContext.member.name} will be unassigned from the{' '}
                  <span className="font-semibold">{SHIFT_CODE[unassignConfirm.shift.shift_type]}</span>{' '}
                  shift on {cellContext.day.date}
                  {unassignConfirm.role === 'secondary'
                    ? ' (secondary slot only — the primary assignment is kept).'
                    : unassignConfirm.shift.secondary_rss_name
                      ? ` (secondary ${unassignConfirm.shift.secondary_rss_name} stays on the shift).`
                      : '. The slot returns to OPEN.'}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUnassignConfirm(null)} data-testid="unassign-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmUnassign} data-testid="unassign-confirm">
              Remove from Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability conflict warning — warns, never blocks */}
      <Dialog open={!!availabilityWarn} onOpenChange={(open) => !open && setAvailabilityWarn(null)}>
        <DialogContent className="sm:max-w-[460px]" data-testid="availability-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Availability Conflict
            </DialogTitle>
            <DialogDescription>
              {availabilityWarn && cellContext && (
                <>
                  <span className="font-semibold">{cellContext.member.name}</span>
                  {availabilityWarn.allDay
                    ? ' marked this entire day as Unavailable'
                    : <>{' marked the '}<span className="font-semibold">{SHIFT_CODE[availabilityWarn.shiftType]}</span>{' shift as Unavailable'}</>}
                  {' in their submitted availability.'}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {availabilityWarn && (availabilityWarn.restriction || availabilityWarn.note) && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3 text-xs text-amber-900 dark:text-amber-200 space-y-0.5">
              {availabilityWarn.restriction && <div>Restriction: {availabilityWarn.restriction}</div>}
              {availabilityWarn.note && <div>Note: {availabilityWarn.note}</div>}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            You can still assign this shift. The override will be recorded in the audit log.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAvailabilityWarn(null)} data-testid="availability-warn-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => { const st = availabilityWarn.shiftType; setAvailabilityWarn(null); handleCellAssign(st, true); }}
              data-testid="availability-warn-confirm"
            >
              Assign Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== SWAP SLOT DISAMBIGUATION ===== */}
      {/* Only shown when admin clicks "Confirm Swap" on a swap_pending
          annotation whose underlying shift is double-staffed. We must ask
          which staff (primary or secondary) is the one being swapped out
          rather than guessing. */}
      <Dialog
        open={!!swapSlotPrompt}
        onOpenChange={(o) => !o && setSwapSlotPrompt(null)}
      >
        <DialogContent className="sm:max-w-md" data-testid="swap-slot-dialog">
          <DialogHeader>
            <DialogTitle>Which staff is being swapped?</DialogTitle>
            <DialogDescription>
              This shift has two staff members. Pick the one whose place will be
              taken by the swap partner.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => finalizeSwapWithSlot('primary')}
              className="justify-start h-auto py-3"
              data-testid="swap-slot-primary"
            >
              <div className="text-left">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Primary</div>
                <div className="font-semibold">{swapSlotPrompt?.primaryName}</div>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => finalizeSwapWithSlot('secondary')}
              className="justify-start h-auto py-3"
              data-testid="swap-slot-secondary"
            >
              <div className="text-left">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Secondary</div>
                <div className="font-semibold">{swapSlotPrompt?.secondaryName}</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSwapSlotPrompt(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedule;
